/**
 * Quiz Section Component - Displays and manages quizzes for articles
 * Features: 10 questions, interview-focused, scoring system, feedback
 */

import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, RotateCcw, Trophy, AlertCircle, Zap, Lock, Crown, Clock } from 'lucide-react';
import type { QuizData, QuizScore } from '@/types';
import { saveQuizScore, getBestScore } from '@/services/quizScoreService';
import { isQuizTrialAvailable, getQuizTrialDaysRemaining } from '@/services/trialService';

interface QuizSectionProps {
  quiz: QuizData;
  articleId: string;
  articleTitle: string;
  userId: string | null;
  isDark: boolean;
  isPro?: boolean;
  onUpgradeClick?: () => void;
  roadmapStartDate?: string | null;
}

type QuizState = 'intro' | 'taking' | 'completed';

export const QuizSection: React.FC<QuizSectionProps> = ({
  quiz,
  articleId,
  articleTitle,
  userId,
  isDark,
  isPro = false,
  onUpgradeClick,
  roadmapStartDate
}) => {
  const [state, setState] = useState<QuizState>('intro');
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [userAnswers, setUserAnswers] = useState<(number | null)[]>([]);
  const [quizScore, setQuizScore] = useState<QuizScore | null>(null);
  const [previousBest, setPreviousBest] = useState<QuizScore | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Load previous best score if user is logged in and is pro
    if (userId && isPro) {
      const best = getBestScore(userId, articleId);
      setPreviousBest(best);
    }
  }, [userId, articleId, isPro]);

  const handleStartQuiz = () => {
    setUserAnswers(new Array(quiz.totalQuestions).fill(null));
    setState('taking');
  };

  const handleAnswerSelect = (answerIndex: number) => {
    const newAnswers = [...userAnswers];
    newAnswers[currentQuestion] = answerIndex;
    setUserAnswers(newAnswers);

    // Auto-advance to next question after a short delay
    setTimeout(() => {
      if (currentQuestion < quiz.totalQuestions - 1) {
        setCurrentQuestion(currentQuestion + 1);
      }
    }, 300);
  };

  const handleSubmitQuiz = async () => {
    // Calculate score
    let correctCount = 0;
    quiz.questions.forEach((q, idx) => {
      if (userAnswers[idx] === q.correctAnswer) {
        correctCount++;
      }
    });

    const score = Math.round((correctCount / quiz.totalQuestions) * 100);
    const isPassed = score >= quiz.passingScore;

    // Save score
    if (userId) {
      setLoading(true);
      try {
        const saved = await saveQuizScore(
          userId,
          articleId,
          articleTitle,
          score,
          quiz.totalQuestions,
          correctCount,
          userAnswers as number[],
          quiz.passingScore
        );
        setQuizScore(saved);
      } catch (error) {
        console.error('Error saving quiz score:', error);
      } finally {
        setLoading(false);
      }
    } else {
      // User not logged in - still show score
      setQuizScore({
        userId: 'anonymous',
        articleId,
        articleTitle,
        score,
        totalQuestions: quiz.totalQuestions,
        correctAnswers: correctCount,
        userAnswers: userAnswers as number[],
        completedAt: new Date().toISOString(),
        isPassed
      });
    }

    setState('completed');
  };

  const handleRetake = () => {
    setCurrentQuestion(0);
    setUserAnswers([]);
    setQuizScore(null);
    setState('intro');
  };

  const currentQ = quiz.questions[currentQuestion];
  const progress = ((currentQuestion + 1) / quiz.totalQuestions) * 100;
  const allAnswered = userAnswers.every(a => a !== null);

  // Trial logic: Quiz available for first 3 days of roadmap
  const isTrialActive = isQuizTrialAvailable(roadmapStartDate);
  const trialDaysRemaining = getQuizTrialDaysRemaining(roadmapStartDate);
  const canAccessQuiz = isPro || isTrialActive;

  return (
    <div className={`rounded-2xl p-6 md:p-8 border-2 relative ${
      isDark ? 'bg-amber-500/5 border-amber-500/30' : 'bg-amber-50/50 border-amber-300/50'
    }`}>
      {/* Trial Expired Lock Overlay */}
      {!canAccessQuiz && (
        <div className={`absolute inset-0 rounded-2xl backdrop-blur-sm flex flex-col items-center justify-center z-10 ${
          isDark ? 'bg-black/40' : 'bg-white/40'
        }`}>
          <div className="text-center space-y-3">
            <Lock className={`w-12 h-12 mx-auto ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
            <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-zinc-900'}`}>
              Quiz Trial Expired
            </h3>
            <p className={`text-sm max-w-xs ${isDark ? 'text-zinc-300' : 'text-zinc-600'}`}>
              Your 3-day free trial has ended. Upgrade to Pro to unlock quizzes and track your progress.
            </p>
            <button
              onClick={() => onUpgradeClick?.()}
              className="mt-4 px-6 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg font-bold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity mx-auto"
            >
              <Crown className="w-4 h-4" />
              Upgrade to Pro
            </button>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className={`text-2xl font-bold mb-2 flex items-center gap-2 ${
            isDark ? 'text-amber-400' : 'text-amber-600'
          }`}>
            <Zap className="w-6 h-6" />
            Assessment Quiz
          </h3>
          <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
            Test your understanding with 10 interview-focused questions
          </p>
        </div>
        {!isPro && isTrialActive && (
          <div className={`px-3 py-1.5 rounded-full flex items-center gap-1.5 text-xs font-bold whitespace-nowrap ${
            isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-700'
          }`}>
            <Clock className="w-3.5 h-3.5" />
            Free: {trialDaysRemaining} day{trialDaysRemaining !== 1 ? 's' : ''} left
          </div>
        )}
      </div>

      {/* Intro State */}
      {state === 'intro' && (
        <div className="space-y-4">
          <div className={`p-4 rounded-lg border ${
            isDark ? 'bg-white/5 border-white/10' : 'bg-white border-amber-200'
          }`}>
            <h4 className={`font-bold mb-3 ${isDark ? 'text-white' : 'text-zinc-900'}`}>
              📋 Quiz Details
            </h4>
            <ul className={`space-y-2 text-sm ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
              <li>✓ <strong>{quiz.totalQuestions} questions</strong> covering core concepts</li>
              <li>✓ Mix of <strong>multiple choice</strong> and <strong>true/false</strong></li>
              <li>✓ <strong>Interview-focused</strong> questions for real-world preparation</li>
              <li>✓ <strong>Instant feedback</strong> on answers with explanations</li>
              <li>✓ Pass with <strong>{quiz.passingScore}%</strong> or higher</li>
            </ul>
          </div>

          {previousBest && (
            <div className={`p-3 rounded-lg border flex items-start gap-3 ${
              previousBest.isPassed 
                ? (isDark ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-emerald-50 border-emerald-300')
                : (isDark ? 'bg-orange-500/10 border-orange-500/30' : 'bg-orange-50 border-orange-300')
            }`}>
              <Trophy className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                previousBest.isPassed ? 'text-emerald-500' : 'text-orange-500'
              }`} />
              <div className="text-sm">
                <p className={`font-bold ${previousBest.isPassed ? 'text-emerald-600' : 'text-orange-600'}`}>
                  Your Best Score: {previousBest.score}%
                </p>
                <p className={`text-xs ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                  {new Date(previousBest.completedAt).toLocaleDateString()}
                </p>
              </div>
            </div>
          )}

          <button
            onClick={handleStartQuiz}
            className="w-full py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg font-bold hover:opacity-90 transition-opacity"
          >
            Start Quiz
          </button>
        </div>
      )}

      {/* Taking Quiz State */}
      {state === 'taking' && (
        <div className="space-y-6">
          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="flex justify-between items-center text-sm">
              <span className={isDark ? 'text-zinc-400' : 'text-zinc-600'}>
                Question {currentQuestion + 1} of {quiz.totalQuestions}
              </span>
              <span className={`font-bold ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>
                {Math.round(progress)}%
              </span>
            </div>
            <div className={`h-2 rounded-full overflow-hidden ${isDark ? 'bg-white/10' : 'bg-amber-200'}`}>
              <div
                className="h-full bg-gradient-to-r from-amber-500 to-orange-500 transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Question Card */}
          <div className={`p-6 rounded-xl border-2 ${
            isDark ? 'bg-white/5 border-amber-500/30' : 'bg-white border-amber-200'
          }`}>
            <div className="mb-6">
              <p className={`text-sm font-semibold mb-2 ${
                currentQ.difficulty === 'hard' ? 'text-red-500' :
                currentQ.difficulty === 'medium' ? 'text-yellow-500' :
                'text-emerald-500'
              }`}>
                {currentQ.difficulty.toUpperCase()} • {currentQ.concept}
              </p>
              <h4 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-zinc-900'}`}>
                {currentQ.question}
              </h4>
            </div>

            {/* Answer Options */}
            <div className="space-y-2">
              {currentQ.options.map((option, idx) => (
                <button
                  key={idx}
                  onClick={() => handleAnswerSelect(idx)}
                  className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                    userAnswers[currentQuestion] === idx
                      ? isDark
                        ? 'bg-amber-500/20 border-amber-500 text-white'
                        : 'bg-amber-100 border-amber-500 text-zinc-900'
                      : isDark
                        ? 'bg-white/5 border-white/10 hover:border-amber-500/50 text-zinc-300'
                        : 'bg-white border-zinc-200 hover:border-amber-500/50 text-zinc-900'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      userAnswers[currentQuestion] === idx
                        ? isDark ? 'bg-amber-500 border-amber-500' : 'bg-amber-500 border-amber-500'
                        : isDark ? 'border-white/20' : 'border-zinc-300'
                    }`}>
                      {userAnswers[currentQuestion] === idx && (
                        <div className="w-2.5 h-2.5 bg-white rounded-full" />
                      )}
                    </div>
                    <span className="text-sm font-medium">{option}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Navigation */}
          <div className="flex gap-3">
            <button
              onClick={() => setCurrentQuestion(Math.max(0, currentQuestion - 1))}
              disabled={currentQuestion === 0}
              className={`flex-1 py-2 rounded-lg font-semibold transition-all ${
                currentQuestion === 0
                  ? isDark ? 'bg-white/5 text-zinc-600 cursor-not-allowed' : 'bg-zinc-100 text-zinc-400 cursor-not-allowed'
                  : isDark ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-zinc-200 hover:bg-zinc-300 text-zinc-900'
              }`}
            >
              ← Previous
            </button>

            {currentQuestion === quiz.totalQuestions - 1 ? (
              <button
                onClick={handleSubmitQuiz}
                disabled={!allAnswered || loading}
                className={`flex-1 py-2 rounded-lg font-bold transition-all ${
                  allAnswered && !loading
                    ? 'bg-gradient-to-r from-emerald-500 to-green-500 text-white hover:opacity-90'
                    : isDark ? 'bg-white/5 text-zinc-600 cursor-not-allowed' : 'bg-zinc-100 text-zinc-400 cursor-not-allowed'
                }`}
              >
                {loading ? 'Saving...' : 'Submit Quiz'}
              </button>
            ) : (
              <button
                onClick={() => setCurrentQuestion(currentQuestion + 1)}
                disabled={userAnswers[currentQuestion] === null}
                className={`flex-1 py-2 rounded-lg font-semibold transition-all ${
                  userAnswers[currentQuestion] !== null
                    ? isDark ? 'bg-amber-500/80 hover:bg-amber-500 text-white' : 'bg-amber-500 hover:bg-amber-600 text-white'
                    : isDark ? 'bg-white/5 text-zinc-600 cursor-not-allowed' : 'bg-zinc-100 text-zinc-400 cursor-not-allowed'
                }`}
              >
                Next →
              </button>
            )}
          </div>
        </div>
      )}

      {/* Completed State */}
      {state === 'completed' && quizScore && (
        <div className="space-y-6">
          {/* Score Display */}
          <div className="text-center">
            <div className={`inline-flex items-center justify-center w-24 h-24 rounded-full mb-4 ${
              quizScore.isPassed
                ? isDark ? 'bg-emerald-500/20' : 'bg-emerald-100'
                : isDark ? 'bg-red-500/20' : 'bg-red-100'
            }`}>
              {quizScore.isPassed ? (
                <CheckCircle className={`w-12 h-12 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
              ) : (
                <AlertCircle className={`w-12 h-12 ${isDark ? 'text-red-400' : 'text-red-600'}`} />
              )}
            </div>

            <h3 className={`text-3xl font-bold mb-2 ${
              quizScore.isPassed
                ? isDark ? 'text-emerald-400' : 'text-emerald-600'
                : isDark ? 'text-red-400' : 'text-red-600'
            }`}>
              {quizScore.score}%
            </h3>

            <p className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-zinc-900'}`}>
              {quizScore.isPassed ? '🎉 You Passed!' : '📚 Keep Learning'}
            </p>

            <p className={`text-sm mb-6 ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
              You answered <strong>{quizScore.correctAnswers}</strong> out of <strong>{quizScore.totalQuestions}</strong> questions correctly
            </p>

            {!quizScore.isPassed && (
              <p className={`text-sm mb-6 ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                You need <strong>{quiz.passingScore}%</strong> to pass. Keep studying and try again!
              </p>
            )}
          </div>

          {/* Review Answers */}
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {quiz.questions.map((q, idx) => (
              <div
                key={idx}
                className={`p-4 rounded-lg border-2 ${
                  userAnswers[idx] === q.correctAnswer
                    ? isDark ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-emerald-50 border-emerald-300'
                    : isDark ? 'bg-red-500/10 border-red-500/30' : 'bg-red-50 border-red-300'
                }`}
              >
                <div className="flex gap-3">
                  {userAnswers[idx] === q.correctAnswer ? (
                    <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-1" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-1" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className={`font-semibold text-sm mb-2 ${isDark ? 'text-white' : 'text-zinc-900'}`}>
                      Q{idx + 1}: {q.question}
                    </p>
                    <p className={`text-xs mb-2 ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                      Your answer: <strong>{q.options[userAnswers[idx] as number]}</strong>
                    </p>
                    {userAnswers[idx] !== q.correctAnswer && (
                      <p className={`text-xs mb-2 ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                        Correct answer: <strong>{q.options[q.correctAnswer]}</strong>
                      </p>
                    )}
                    <p className={`text-xs italic ${isDark ? 'text-zinc-500' : 'text-zinc-600'}`}>
                      💡 {q.explanation}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleRetake}
              className="flex-1 py-3 rounded-lg font-bold bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
            >
              <RotateCcw className="w-4 h-4" />
              Retake Quiz
            </button>
          </div>

          {!userId && (
            <div className={`p-3 rounded-lg border text-center text-sm ${
              isDark ? 'bg-blue-500/10 border-blue-500/30 text-blue-300' : 'bg-blue-50 border-blue-300 text-blue-700'
            }`}>
              📝 <strong>Tip:</strong> Sign in to save your quiz scores and track progress!
            </div>
          )}
        </div>
      )}
    </div>
  );
};
